import { Component, inject, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UserAuthService } from '@shared/modules/auth/auth-data-access/user-auth.service';
import { User } from '@shared/api/api-loop/models/user';
import { SettingColumnComponent } from '@shared/modules/main/user-settings/settings-base/settings-column/setting-column.component';
import { ToggleSwitchControlComponent } from '@shared/modules/form-controls/toggle-switch-control/toggle-switch-control.component';
import { StorageKey, StorageService } from '@dta/shared/services/storage/storage.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { map } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'user-settings-general',
  templateUrl: './general.html',
  styleUrls: ['./general.scss'],
  host: { class: 'main' },
  imports: [NgIf, SettingColumnComponent, ToggleSwitchControlComponent, FormsModule]
})
export class UserSettingsGeneralComponent implements OnInit {
  private _storageService: StorageService = inject(StorageService);
  private readonly userAuthService: UserAuthService = inject(UserAuthService);

  private readonly userSignal: Signal<User | null> = toSignal(
    this.userAuthService.currentSession$.pipe(map(session => session.user)),
    { initialValue: null }
  );

  notificationSettings: INotificationSettings;
  protected readonly isNotificationPermissionAllowedSignal: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly areNotificationsAllowedSignal: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly isRequestingPermissionSignal: WritableSignal<boolean> = signal<boolean>(false);

  ngOnInit(): void {
    this.setNotificationPermission();
    this.init();
  }

  onAllowNotificationToggle(newValue?: boolean, calledFromPermissionCallback: boolean = false): void {
    if (newValue !== undefined) {
      this.areNotificationsAllowedSignal.set(newValue);
    }

    if (this.areNotificationsAllowedSignal() || Notification.permission === 'granted') {
      this.notificationSettings.allowNotifications = this.areNotificationsAllowedSignal();
      this.isRequestingPermissionSignal.set(false);
      this.onNotificationChange();
    } else if (Notification.permission === 'denied') {
      this.areNotificationsAllowedSignal.set(false);
      this.notificationSettings.allowNotifications = false;
      this.isRequestingPermissionSignal.set(false);
      this.onNotificationChange();
    } else if (Notification.permission === 'default') {
      if (!calledFromPermissionCallback) {
        this.requestNotificationPermission();
      }
    }

    this.setNotificationPermission();
  }

  onNotificationChange(): void {
    this.setUserNotificationSettingsByEmail(this.notificationSettings);
  }

  ///////////////////
  // Private methods
  ///////////////////

  private init(): void {
    this.notificationSettings = this.getUserNotificationSettingsByEmail() || new INotificationSettings();
    this.areNotificationsAllowedSignal.set(this.notificationSettings.allowNotifications);
    this.isRequestingPermissionSignal.set(false);
  }

  private setNotificationPermission(): void {
    this.isNotificationPermissionAllowedSignal.set(Notification.permission !== 'denied');
  }

  private requestNotificationPermission(): void {
    this.isRequestingPermissionSignal.set(true);

    /**
     * Notification.requestPermission() returns a promise
     * Safari still uses the deprecated callback syntax to get the permission
     * https://developer.mozilla.org/en-US/docs/Web/API/Notification/requestPermission
     */
    try {
      Notification.requestPermission().then(() => {
        this.onAllowNotificationToggle(undefined, true);
      });
    } catch (_error) {
      Notification.requestPermission(() => {
        this.onAllowNotificationToggle(undefined, true);
      });
    }
  }

  private getUserNotificationSettingsByEmail(): INotificationSettings {
    let key = this._storageService.getKey(this.userSignal()?.email, StorageKey.userNotificationSettings);
    return this._storageService.getParsedItem(key);
  }

  private setUserNotificationSettingsByEmail(settings: INotificationSettings): void {
    let key = this._storageService.getKey(this.userSignal()?.email, StorageKey.userNotificationSettings);
    return this._storageService.setStringifiedItem(key, settings);
  }
}

export class INotificationSettings {
  allowNotifications: boolean;
  notifications: {
    chat: boolean;
    email: boolean;
    teamEmail: boolean;
  };
  notificationSound: boolean;
  isNotifyWhenFocusedEnabled: boolean;

  constructor() {
    this.setDefaultValues();
  }

  setDefaultValues(): void {
    this.allowNotifications = false;
    this.notifications = {
      chat: true,
      email: true,
      teamEmail: true
    };
    this.notificationSound = false;
    this.isNotifyWhenFocusedEnabled = false;
  }
}
